import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { dismissUpdate, subscribeUpdate, updateState } from './update-state';
import './update-banner.css';

export interface UpdateBannerProps {
  /** Why reloading now would lose work, or null (reload-hold.ts). */
  held: () => string | null;
  reload: () => void;
}

/**
 * The one line that says a newer Paige is deployed while someone stays on one screen: small, at the
 * foot of the window, never in the way and never taking focus (docs/engineering/web.md, "When
 * Paige is updated"). Reload is the person's to press. If work on the screen would be lost, the
 * first press says so instead and the second reloads; a guard that asks before leaving still asks.
 */
export function UpdateBanner({ held, reload }: UpdateBannerProps): ReactNode {
  const state = useSyncExternalStore(subscribeUpdate, updateState, updateState);
  const [warned, setWarned] = useState(false);
  if (!state.updated || state.dismissed) return null;
  const onReload = () => {
    if (!warned && held() !== null) setWarned(true);
    else reload();
  };
  return (
    <div className="update-banner" role="status" aria-live="polite">
      <span className="update-banner__text">
        {warned ? 'Save or finish your work here first, then reload.' : 'Paige was updated.'}
      </span>
      <button type="button" className="update-banner__reload" onClick={onReload}>
        {warned ? 'Reload anyway' : 'Reload'}
      </button>
      <button
        type="button"
        className="update-banner__close"
        aria-label="Not now"
        title="Not now"
        onClick={() => {
          setWarned(false);
          dismissUpdate();
        }}
      >
        {'×'}
      </button>
    </div>
  );
}
